import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Users, Sparkles, CalendarDays, BookOpen, TrendingUp, Globe } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Reveal } from "../components/Reveal";
import { PROGRAMS } from "../data/site";

const ICONS = { BookOpen, TrendingUp, Globe };

export default function Programs() {
  return (
    <div data-testid="programs-page" className="pt-32">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12">
        <Reveal>
          <span className="font-bold text-teal-brand">PROGRAMS</span>
          <h1 className="font-display font-extrabold text-5xl sm:text-7xl text-navy mt-2 leading-[0.95]">
            Three curricula.<br />One team of <span className="text-marigold">specialists.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-navy/70">
            Choose a track to see subjects, who it&apos;s for, what makes it
            different, and a sample week.
          </p>
        </Reveal>
      </section>

      {/* Tabbed programs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24" data-testid="programs-tabs">
        <Tabs defaultValue={PROGRAMS[0].id} className="w-full">
          <TabsList className="w-full h-auto flex-wrap justify-start gap-2 bg-transparent p-0 mb-8">
            {PROGRAMS.map((p) => {
              const Icon = ICONS[p.icon];
              return (
                <TabsTrigger
                  key={p.id}
                  value={p.id}
                  data-testid={`tab-${p.id}`}
                  className="rounded-full border-2 border-navy/15 bg-white px-5 py-3 font-display font-bold text-navy data-[state=active]:bg-navy data-[state=active]:text-cream data-[state=active]:border-navy transition-colors"
                >
                  {Icon && <Icon size={18} className="mr-2" />}
                  {p.title}
                  <span className="ml-2 text-xs font-semibold opacity-70">{p.tag}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {PROGRAMS.map((p) => {
            const Icon = ICONS[p.icon];
            return (
            <TabsContent key={p.id} value={p.id} data-testid={`panel-${p.id}`}>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="grid lg:grid-cols-2 gap-8 items-stretch"
              >
                {/* Left visual card */}
                <div
                  className="rounded-[2rem] p-10 text-white relative overflow-hidden"
                  style={{ backgroundColor: p.color }}
                >
                  <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10" />
                  {Icon && <Icon size={44} className="mb-6" />}
                  <span className="inline-block rounded-full bg-white/20 px-4 py-1.5 text-sm font-bold">{p.tag}</span>
                  <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-4">{p.title}</h2>
                  <p className="mt-4 text-white/90 text-lg">{p.hook}</p>
                  <div className="mt-8">
                    <p className="text-white/70 font-bold text-sm mb-3">SUBJECTS</p>
                    <div className="flex flex-wrap gap-2">
                      {p.subjects.map((s) => (
                        <span key={s} className="rounded-full bg-white/15 px-4 py-2 text-sm font-semibold">{s}</span>
                      ))}
                    </div>
                  </div>
                  <Link
                    to={`/admissions?program=${encodeURIComponent(p.title)}`}
                    data-testid={`enquire-${p.id}`}
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy text-cream font-bold px-7 py-3.5 hover:bg-cream hover:text-navy transition-colors"
                  >
                    Enquire for {p.title} <ArrowRight size={18} />
                  </Link>
                </div>

                {/* Right detail */}
                <div className="rounded-[2rem] bg-white border border-navy/10 p-8 sm:p-10 space-y-7">
                  <div>
                    <div className="flex items-center gap-2 text-navy/50 font-bold text-sm mb-2">
                      <Users size={16} /> WHO IT&apos;S FOR
                    </div>
                    <p className="text-navy/80 text-lg">{p.who}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-navy/50 font-bold text-sm mb-2">
                      <Sparkles size={16} /> WHAT MAKES IT DIFFERENT
                    </div>
                    <p className="text-navy/80 text-lg">{p.different}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-navy/50 font-bold text-sm mb-3">
                      <CalendarDays size={16} /> SAMPLE WEEKLY STRUCTURE
                    </div>
                    <ul className="space-y-2">
                      {p.weekly.map((w) => (
                        <li key={w} className="flex items-start gap-3 text-navy/80">
                          <span className="grid place-items-center w-6 h-6 rounded-full text-white shrink-0 mt-0.5" style={{ backgroundColor: p.color }}>
                            <Check size={14} />
                          </span>
                          {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </TabsContent>
          );})}
        </Tabs>
      </section>

      {/* CTA */}
      <section className="py-20 bg-navy text-cream">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl">
              Not sure which track fits?
            </h2>
            <p className="mt-4 text-cream/75 text-lg">
              A 30-minute consultation is all it takes. We&apos;ll help you decide — free.
            </p>
            <Link
              to="/admissions"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-marigold text-white font-bold text-lg px-8 py-4 hover:bg-teal-brand transition-colors"
              data-testid="programs-cta"
            >
              Book a Free Consultation <ArrowRight size={20} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
